import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { resolveAssetUrl } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Trophy, Lock, Globe, Upload, X, Loader2, ImageIcon, ArrowLeft, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { ClubPicker } from "@/components/ClubPicker";

interface LeagueFormData {
  name: string;
  description: string;
  isPrivate: boolean;
  matchFormat: "singles" | "doubles";
  imageUrl?: string;
  homeClubId?: string | null;
  ratingInitMode: "skill_based" | "equal";
}

type ProfileLike = {
  country?: string | null;
  homeClubId?: string | null;
} | null | undefined;

export function CreateLeaguePage({ onCreateLeague }: { onCreateLeague?: (data: LeagueFormData) => void }) {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const profile = user?.profile as ProfileLike;
  const profileCountry = profile?.country || null;
  const profileHomeClubId = profile?.homeClubId || null;
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState<LeagueFormData>({
    name: "",
    description: "",
    isPrivate: true,
    matchFormat: "doubles",
    imageUrl: "",
    homeClubId: null,
    ratingInitMode: "skill_based",
  });
  const [showRatingModeHelp, setShowRatingModeHelp] = useState(false);
  const [clubTouched, setClubTouched] = useState(false);
  useEffect(() => {
    if (clubTouched) return;
    if (profileHomeClubId && !formData.homeClubId) {
      setFormData((f) => ({ ...f, homeClubId: profileHomeClubId }));
    }
  }, [profileHomeClubId, clubTouched, formData.homeClubId]);
  const { toast } = useToast();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await fetch('/api/upload/league-image', {
        method: 'POST',
        body: formDataUpload,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al subir la imagen');
      }

      const data = await response.json();
      setFormData({ ...formData, imageUrl: data.url });
      toast({
        title: "Imagen subida",
        description: "La imagen se ha subido correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo subir la imagen",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreateLeague?.(formData);
    setFormData({
      name: "",
      description: "",
      isPrivate: true,
      matchFormat: "doubles",
      imageUrl: "",
      homeClubId: null,
      ratingInitMode: "skill_based",
    });
    history.back();
  };

  return (
    <div className="min-h-screen bg-background">
      <header 
        className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="container max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => history.back()}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Crear Liga</h1>
        </div>
      </header>

      <main className="container max-w-2xl mx-auto px-4 pt-3 safe-area-bottom">
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Los campos marcados con <span className="text-destructive">*</span> son obligatorios.
          </p>

          <div className="space-y-2">
            <Label htmlFor="league-name">
              Nombre de la Liga <span className="text-destructive">*</span>
            </Label>
            <Input
              id="league-name"
              placeholder="ej., Guerreros del Fin de Semana"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="input-league-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="league-description">Descripción</Label>
            <Textarea
              id="league-description"
              placeholder="Describe tu liga..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              data-testid="input-league-description"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Imagen de la Liga (Opcional)
            </Label>
            <div className="flex items-center gap-2">
              <label
                htmlFor="create-league-image-upload"
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-3 cursor-pointer transition-colors",
                  "hover:border-primary hover:bg-primary/5",
                  uploadingImage && "opacity-50 cursor-not-allowed"
                )}
              >
                {uploadingImage ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-sm text-muted-foreground">Subiendo...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {formData.imageUrl ? "Cambiar imagen" : "Subir imagen"}
                    </span>
                  </>
                )}
                <input
                  id="create-league-image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  data-testid="input-create-league-image-upload"
                />
              </label>
              {formData.imageUrl && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setFormData({ ...formData, imageUrl: "" })}
                  data-testid="button-remove-create-league-image"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {formData.imageUrl && (
              <div className="mt-2 rounded-lg overflow-hidden border">
                <img 
                  src={resolveAssetUrl(formData.imageUrl)} 
                  alt="Preview" 
                  className="w-full h-24 object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Formato</Label>
            <Select
              value={formData.matchFormat}
              onValueChange={(v) =>
                setFormData({ ...formData, matchFormat: v as "singles" | "doubles" })
              }
            >
              <SelectTrigger data-testid="select-match-format">
                <SelectValue placeholder="Formato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="doubles">Dobles</SelectItem>
                <SelectItem value="singles">Singles</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label>Modo de inicio del ranking</Label>
              <Popover open={showRatingModeHelp} onOpenChange={setShowRatingModeHelp}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-5 w-5"
                    data-testid="button-rating-mode-help-create-league"
                  >
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="max-w-xs text-sm space-y-1.5">
                  <p>
                    <strong>Por nivel:</strong> los jugadores con mejor rating global empiezan con ventaja en la tabla.
                  </p>
                  <p>
                    <strong>Parejo:</strong> todos arrancan en 1000 puntos y el ranking se define solo por los partidos jugados en esta liga.
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            <RadioGroup
              value={formData.ratingInitMode}
              onValueChange={(v) =>
                setFormData({ ...formData, ratingInitMode: v as "skill_based" | "equal" })
              }
              className="gap-2"
            >
              <label className="flex items-center gap-2 rounded-md border p-2.5 cursor-pointer hover:bg-muted/50">
                <RadioGroupItem value="skill_based" data-testid="radio-rating-mode-skill-create-league" />
                <span className="text-sm">Por nivel</span>
              </label>
              <label className="flex items-center gap-2 rounded-md border p-2.5 cursor-pointer hover:bg-muted/50">
                <RadioGroupItem value="equal" data-testid="radio-rating-mode-equal-create-league" />
                <span className="text-sm">Parejo (todos empiezan en 1000)</span>
              </label>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Club sede (opcional)</Label>
            <ClubPicker
              value={formData.homeClubId || null}
              onChange={(id) => {
                setClubTouched(true);
                setFormData({ ...formData, homeClubId: id });
              }}
              country={profileCountry}
              testId="create-league-club-picker"
            />
            <p className="text-xs text-muted-foreground">
              Si la mayoría de partidos se juegan en un club concreto, selecciónalo aquí.
            </p>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              {formData.isPrivate ? (
                <Lock className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Globe className="h-4 w-4 text-muted-foreground" />
              )}
              <div>
                <Label htmlFor="private-league" className="font-medium">
                  Liga Privada
                </Label>
                <p className="text-xs text-muted-foreground">
                  {formData.isPrivate
                    ? "Solo miembros invitados pueden unirse"
                    : "Cualquiera puede solicitar unirse"}
                </p>
              </div>
            </div>
            <Switch
              id="private-league"
              checked={formData.isPrivate}
              onCheckedChange={(c) => setFormData({ ...formData, isPrivate: c })}
              data-testid="switch-private-league"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => history.back()}
              data-testid="button-cancel-league"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={!formData.name.trim()}
              data-testid="button-submit-league"
            >
              Crear Liga
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
